import { useCallback, useState } from 'react'

import { Marker, type MarkerDragEvent, type PointLike } from '@vis.gl/react-maplibre'

import ApartmentMarkerOrange from '../../assets/ApartmentMarkerOrange.svg'
import ApartmentMarkerOrangeRaised from '../../assets/ApartmentMarkerOrangeRaised.svg'
import HouseMarkerOrange from '../../assets/HouseMarkerOrange.svg'
import HouseMarkerOrangeRaised from '../../assets/HouseMarkerOrangeRaised.svg'
import OfficeMarkerOrange from '../../assets/OfficeMarkerOrange.svg'
import OfficeMarkerOrangeRaised from '../../assets/OfficeMarkerOrangeRaised.svg'
import PackageMarkerGreenBgCircle from '../../assets/PackageMarkerGreenBgCircle.svg'
import PhotoMarkerOrangeBg from '../../assets/PhotoMarkerOrangeBg.svg'

export type MarkerVariants = 'house' | 'apartment' | 'business' | 'photo' | 'package'

export interface MapMarkerProps {
  variant: MarkerVariants
  position: {
    lat: number
    lng: number
  }
  onClick?: () => void
  draggable?: boolean
  onDragStart?: (dragEvent: MarkerDragEvent) => void
  onDrag?: (dragEvent: MarkerDragEvent) => void
  onDragEnd?: (dragEvent: MarkerDragEvent) => void
  showPopup?: boolean
  onPopupPress?: () => void
  offset?: number | PointLike
  zIndex?: number
}

const MapMarker: React.FC<MapMarkerProps> = ({
  variant,
  position,
  onClick,
  draggable,
  onDragStart,
  onDrag,
  onDragEnd,
  offset,
  zIndex,
}) => {
  const [markerRaised, setMarkerRaised] = useState<boolean>(false)
  const [markerCoords, setMarkerCoords] = useState({ latitude: position.lat, longitude: position.lng })

  const mapMarkerIcon = useCallback(
    (markerType: MarkerVariants) => {
      let marker = ''
      switch (markerType) {
        // Draggable map markers
        case 'house':
          marker = markerRaised ? HouseMarkerOrangeRaised : HouseMarkerOrange
          break
        case 'apartment':
          marker = markerRaised ? ApartmentMarkerOrangeRaised : ApartmentMarkerOrange
          break
        case 'business':
          marker = markerRaised ? OfficeMarkerOrangeRaised : OfficeMarkerOrange
          break
        // Static map markers
        case 'photo':
          marker = PhotoMarkerOrangeBg
          break
        case 'package':
          marker = PackageMarkerGreenBgCircle
          break
        default:
          marker = HouseMarkerOrange
      }
      return marker
    },
    [markerRaised]
  )

  const handleMarkerOnDragStart = useCallback(
    (dragEvent: MarkerDragEvent) => {
      setMarkerRaised(true)

      if (onDragStart) {
        onDragStart(dragEvent)
      }
    },
    [onDragStart]
  )

  const handleMarkerOnDrag = useCallback(
    (dragEvent: MarkerDragEvent) => {
      if (onDrag) {
        onDrag(dragEvent)
      }
      setMarkerCoords({
        latitude: dragEvent.lngLat.lat,
        longitude: dragEvent.lngLat.lng,
      })
    },
    [onDrag]
  )

  const handleMarkerOnDragEnd = useCallback(
    (dragEvent: MarkerDragEvent) => {
      setMarkerRaised(false)
      if (onDragEnd) {
        onDragEnd(dragEvent)
      }
    },
    [onDragEnd]
  )

  // TO DO:
  // 1. Add onClick handler for for MarkerVariant photo to open photo viewer
  return (
    <Marker
      latitude={markerCoords.latitude}
      longitude={markerCoords.longitude}
      onClick={onClick}
      draggable={draggable}
      onDragStart={handleMarkerOnDragStart}
      onDrag={handleMarkerOnDrag}
      onDragEnd={handleMarkerOnDragEnd}
      offset={offset as PointLike | undefined}
      style={{ zIndex: zIndex }}
    >
      <img src={mapMarkerIcon(variant)} alt={`map-marker-${variant}`}></img>
    </Marker>
  )
}

export default MapMarker
